import React from 'react';
import { MaterialSku, SKU_PROP_RAW } from '../api/material';
import {
    SkuSpecDraft,
    specFieldsOf,
    summarizeSkuSpec,
    useSkuSchemas,
} from '../utils/skuSpec';
import SearchSelect from './SearchSelect';

// SKU 规格 (2026-10-14, Schema 驱动): 选规格类型 → 按模板渲染字段. 原材料必须选类型并填齐必填项.
// SKU 新建 / 编辑弹窗、材料详情 SKU 表单、新建材料向导共用; 状态与校验在 utils/skuSpec.

/** 规格输入: 类型下拉 + 该类型字段 (带单位 / 必填标记). */
export function SkuSpecInput({
    prop,
    value,
    onChange,
}: {
    prop: string;
    value: SkuSpecDraft;
    onChange: (v: SkuSpecDraft) => void;
}) {
    const { list, byKey } = useSkuSchemas();
    const required = prop === SKU_PROP_RAW;
    const cur = byKey.get(value.group);
    // 下拉只列启用的类型; 存量值引用的是已停用类型时也列出 (标注停用), 免得打开就被悄悄换掉
    const options = list.filter(
        (g) => g.isEnabled || g.groupKey === value.group,
    );
    const fields = specFieldsOf(cur);
    return (
        <div className="space-y-2">
            <div>
                <label className="field-label">
                    规格
                    {required && (
                        <span className="text-rose-500"> * 原材料必填</span>
                    )}
                </label>
                <SearchSelect
                    className="w-full"
                    value={value.group}
                    onChange={(v) =>
                        // 换类型: 旧字段不属于新类型, 清空重填
                        onChange({ group: v, values: {} })
                    }
                    placeholder={required ? '请选择规格类型' : '不填规格'}
                    options={options.map((g) => ({
                        value: g.groupKey,
                        label: g.isEnabled ? g.label : `${g.label} (已停用)`,
                    }))}
                    clearable={!required}
                />
            </div>
            {fields.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className="text-xs text-slate-500 block mb-0.5">
                                {f.required && (
                                    <span className="text-rose-500">* </span>
                                )}
                                {f.label}
                            </label>
                            {f.options?.length ? (
                                <SearchSelect
                                    className="w-full"
                                    value={value.values[f.key] || ''}
                                    onChange={(v) =>
                                        onChange({
                                            ...value,
                                            values: {
                                                ...value.values,
                                                [f.key]: v,
                                            },
                                        })
                                    }
                                    placeholder="—"
                                    options={f.options.map((o) => ({
                                        value: o,
                                        label: o,
                                    }))}
                                    clearable
                                />
                            ) : (
                                <div className="relative">
                                    <input
                                        className={`inp w-full ${f.unit ? 'pr-12' : ''}`}
                                        inputMode={
                                            f.type === 'str'
                                                ? undefined
                                                : 'decimal'
                                        }
                                        value={value.values[f.key] || ''}
                                        onChange={(e) =>
                                            onChange({
                                                ...value,
                                                values: {
                                                    ...value.values,
                                                    [f.key]: e.target.value,
                                                },
                                            })
                                        }
                                        aria-label={f.label}
                                    />
                                    {f.unit && (
                                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                                            {f.unit}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** 只读摘要: 类型名 + "长 300mm · 宽 200mm …"; 原材料未选 / 必填缺项标红「待补规格」. */
export function SkuSpecText({ sku }: { sku: Partial<MaterialSku> }) {
    const { byKey } = useSkuSchemas();
    const s = summarizeSkuSpec(sku, byKey);
    if (!s.label) {
        return s.missing ? (
            <span className="text-rose-500 text-xs">待补规格</span>
        ) : (
            <span className="text-slate-300">—</span>
        );
    }
    return (
        // 类型标签独占一行; 每个「字段 值」各占一行且不断行 (避免 "长 / 304.8mm" 被拆开)
        <div className="text-xs text-slate-600 space-y-1">
            <span className="tag bg-slate-100 text-slate-600">{s.label}</span>
            <div className="space-y-0.5">
                {s.parts.length > 0 ? (
                    s.parts.map((t) => (
                        <div key={t} className="whitespace-nowrap">
                            {t}
                        </div>
                    ))
                ) : (
                    <span className="text-slate-300">未填</span>
                )}
                {s.missing && (
                    <div className="text-rose-500 whitespace-nowrap">待补</div>
                )}
            </div>
        </div>
    );
}
